'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import ModalCloseButton from '@/components/Modal/ModalCloseButton'
import ImageComponent from '@/components/UiElements/ImageComponent'
import transparencyCheckerboardStyle from '@/components/UiElements/transparencyCheckerboardStyle'

type DesignElementImageProps = {
  src: string
  alt: string
  /** Page to show when the raw design is a multi-page PDF shared across sections. */
  page?: number
}

const DesignElementImage = ({ src, alt, page }: DesignElementImageProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    if (!isFullscreen) return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFullscreen(false)
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullscreen])

  const closeFullscreen = () => setIsFullscreen(false)

  const fullscreenPreview =
    isFullscreen &&
    createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-white p-xl"
        role="dialog"
        aria-modal="true"
        aria-label={`${alt} fullscreen preview`}
        onClick={closeFullscreen}
      >
        <div
          className="absolute right-xl top-xl rounded-full bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
        >
          <ModalCloseButton onClick={closeFullscreen} />
        </div>

        <button
          type="button"
          className="flex h-[calc(100vh-64px)] w-[calc(100vw-64px)] cursor-zoom-out items-center justify-center"
          style={transparencyCheckerboardStyle}
          aria-label={`Close ${alt} fullscreen preview`}
          onClick={closeFullscreen}
        >
          <ImageComponent
            src={src}
            page={page}
            className="!h-full !w-full !object-contain"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </button>
      </div>,
      document.body
    )

  return (
    <>
      <button
        type="button"
        className="flex h-[150px] w-[150px] shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-md border border-gray-200 p-sm"
        style={transparencyCheckerboardStyle}
        aria-label={`Open ${alt} fullscreen preview`}
        onClick={() => setIsFullscreen(true)}
      >
        <ImageComponent
          src={src}
          page={page}
          className="!h-full !w-full !object-contain"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </button>

      {fullscreenPreview}
    </>
  )
}

export default DesignElementImage
